import React, { useEffect, useState, useMemo, useCallback } from "react";
import AsyncSelect from "react-select/async";
import { CsrAPI } from "../apis/CsrAPI";

/**
 * StudySelectDropdown Component
 *
 * A single-select dropdown component for selecting studies using react-select with async loading.
 * This component fetches studies from CsrAPI.getAllStudies and provides a searchable dropdown.
 *
 * Props:
 * - selectedValue: String value of the selected study
 * - onChange: Function to handle selection changes (value: string) => void
 * - placeholder: Optional placeholder text
 * - isDisabled: Optional boolean to disable the dropdown
 * - className: Optional CSS class name
 */
const StudySelectDropdown = ({
  selectedValue = "",
  onChange,
  placeholder = "Select a study...",
  isDisabled = false,
  className = "",
}) => {
  const [studies, setStudies] = useState([]);
  const [defaultOptionsList, setDefaultOptionsList] = useState([]);
  useEffect(() => {
    // Initial load of studies
    const fetchStudies = async () => {
      try {
        const allStudies = await CsrAPI.getAllStudiesForLaySummaries();
        setStudies(allStudies);
      } catch (error) {
        console.error("Error fetching studies:", error);
      }
    };
    fetchStudies();
  }, []);
  const loadingMessage = "Loading studies...";

  // Convert selectedValue to react-select format
  const selectedOption = selectedValue
    ? { value: selectedValue, label: selectedValue, studyId: selectedValue }
    : null;

  // Convert studies to react-select format for defaultOptions
  const defaultOptions = useMemo(() => {
    const map = studies.map(study => ({
      value: study.name,
      label: study.name,
      studyId: study.study_id,
    }));
    setDefaultOptionsList(map);
    return map;
  }, [studies]);

  // Handle selection changes
  const handleChange = selectedOption => {
    if (!selectedOption) {
      onChange("");
      return;
    }
    if (!selectedOption.studyId) {
      onChange({
        ...selectedOption,
        studyId: defaultOptionsList.find(
          study => study.value === selectedOption.value
        )?.studyId,
      });
    } else {
      onChange(selectedOption);
    }

    // Extract value from selected option
    // onChange(selectedOption);
  };

  // Load studies from API - memoized to prevent unnecessary re-renders
  const loadStudies = useCallback(
    async inputValue => {
      try {
        // Filter studies based on input value (case-insensitive)
        const filteredStudies = studies.filter(study =>
          study.name.toLowerCase().includes(inputValue.toLowerCase())
        );

        // Convert to react-select format
        const options = filteredStudies.map(study => ({
          value: study.name,
          label: study.name,
        }));

        return options;
      } catch (error) {
        console.error("StudySelectDropdown - Error loading studies:", error);
        return [];
      }
    },
    [studies]
  ); // Dependencies: studies

  // Custom styles for react-select to match the application's design
  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: "50px",
      width: "550px",
      border: state.isFocused ? "1px solid #86b7fe" : "1px solid #ced4da",
      borderRadius: "0.375rem",
      boxShadow: state.isFocused
        ? "0 0 0 0.25rem rgba(13, 110, 253, 0.25)"
        : "none",
      "&:hover": {
        border: "1px solid #86b7fe",
      },
      opacity: isDisabled ? 0.6 : 1,
      pointerEvents: isDisabled ? "none" : "auto",
    }),
    menu: provided => ({
      ...provided,
      zIndex: 9999,
    }),
    container: provided => ({
      ...provided,
      width: "100%",
    }),
  };

  return (
    <div className={`study-select-dropdown-container ${className}`}>
      <AsyncSelect
        isMulti={false}
        loadOptions={loadStudies}
        value={selectedOption}
        onChange={handleChange}
        styles={customStyles}
        placeholder={placeholder}
        isClearable={true}
        isSearchable={true}
        isDisabled={isDisabled}
        className={className || "study-select-dropdown"}
        classNamePrefix="study-select-dropdown"
        noOptionsMessage={() => "No studies found"}
        loadingMessage={() => loadingMessage}
        cacheOptions={true}
        defaultOptions={defaultOptions}
      />
    </div>
  );
};

export default StudySelectDropdown;
